// @flow
import React from 'react'
import { connect } from 'react-redux'
import I18n from 'i18n-react'

import csLanguage from '../Translations/cs.json'

// components
import FormClient from '../Components/FormClient'

// redux
import CarActions from '../Redux/CarRedux'
import GeneralActions from '../Redux/GeneralRedux'
import ValidationActions from '../Redux/ValidationRedux'

// types
import type { GlobalState } from '../store/reducers'
import type { ErrorType } from '../Types/ErrorTypes'

type CarRequestType = {
  modalRZ: string,
  modalVIN: string,
  modalPhone: string,
  modalMileage: string,
}

type StateProps = {
  autoFinish: boolean,
  carInfo: Object,
  carInfoRequest: (CarRequestType, boolean, boolean, string) => void,
  carValuationRequest: (string) => void,
  config: Array<string>,
  data: CarRequestType & { verificationCode: string },
  errors: Array<ErrorType>,
  fetching: number,
  identificated: any,
  isBackOffice?: boolean,
  isCarValuationView?: boolean,
  isWindscreenView?: boolean,
  onChangeValidationError: (string, any) => void,
  onChangeValue: (string, string) => void,
  onCreate?: (Function, Function) => void,
  onErrorsReset: () => void,
  onFinish: (finished: boolean, Object, ?string) => void,
  onSetVerificationCode?: (string) => void,
  remoteValidationErrors: Object,
  renderBottomContent?: () => Element,
  setData: (any) => void,
  showMileagePush?: boolean,
  showPhoneValidation: boolean,
  validationErrors: Object,
  verificationSmsRequest: (string) => void,
  vinFromRzRequest: (string, boolean, boolean, string) => void,
  windscreenPrices: Object,
}

type ScreenState = {
  pushIsPristine: boolean,
  openSuccessErrorModal: boolean,
}

class ClientContainer extends React.Component<StateProps, ScreenState> {
  modalMileage = React.createRef()
  modalPhone = React.createRef()
  modalVin = React.createRef()
  verificationCode = React.createRef()

  static defaultProps = {
    isBackOffice: false,
    isCarValuationView: false,
  }

  constructor(props) {
    super(props)
    I18n.setTexts({
      ...I18n.texts,
      ...csLanguage,
    })
    this.state = {
      pushIsPristine: true,
      openSuccessErrorModal: true,
    }
  }

  componentDidMount() {
    this.props.onCreate && this.props.onCreate(this.props.setData, this.handleSetError)
  }

  componentDidUpdate() {
    if (this.props.autoFinish) {
      document
        .getElementById('error-placeholder')
        .scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
      this.props.setAutoFinish(false)
      this.handleSelect(this.props.carInfo)
      this.setState({
        openSuccessErrorModal: true,
      })
    }
  }

  handleChange = (key: string, value: string) => {
    if (key === 'marketing') {
      this.props.onChangeMarketing(value)
    }
    this.props.onChangeValue(key, value)
  }

  handleOnBlur = (key: string, value: any) => {
    const vinRz = `${value}`.trim().replace(' ', '')
    if (vinRz.length !== 17 && (vinRz.length > 8 || vinRz.length < 4)) {
      this.handleSetError({
        vinRz: {
          error_type: 'invalid_format',
        },
      })
    } else {
      this.handleSetError({
        vinRz: {},
      })
    }
  }

  handleChangeValidationError = (key: string, value: any) => {
    this.props.onChangeValidationError(key, value)
  }

  handleSelect = (vehicle: any, selected: string, withVat: boolean = false) => {
    if (this.props.onFinish) {
      this.props.onFinish(
        true,
        {
          modalMileage: this.props.data.modalMileage,
          modalPhone: this.props.isBackOffice ? '' : this.props.data.modalPhone,
          modalRZ: this.props.data.modalRZ,
          modalVIN: this.props.data.modalVIN,
          technicalCardNo: this.props.data.technicalCardNo,
          ...vehicle,
        },
        selected,
        withVat,
      )
    }
  }

  handleSetError = (errors: Object, pushIsPristine: boolean) => {
    this.props.onSetErrors(errors)
    this.setState({ pushIsPristine })
  }

  handleClose = () => {
    this.setState({
      openSuccessErrorModal: false,
    })
  }

  handleSubmit = (e) => {
    e.preventDefault()
    const { miniAppData } = this.props
    let { vinRz } = this.props.data
    vinRz = `${vinRz}`.trim().replace(' ', '')
    this.props.onErrorsReset()
    if (vinRz.length === 17) {
      if (miniAppData && typeof miniAppData.addGtmEvent === 'function') {
        miniAppData.addGtmEvent('Send', 'Cebia')
      }
      this.props.carInfoRequest(`${vinRz}`.trim().replace(' ', ''), true, 'vehicleInfo')
    } else if (vinRz.length <= 8 && vinRz.length > 5) {
      if (miniAppData && typeof miniAppData.addGtmEvent === 'function') {
        miniAppData.addGtmEvent('Send', 'Cebia')
      }
      this.props.vinFromRzRequest(`${vinRz}`.trim().replace(' ', ''), true, 'vehicleInfo')
    } else {
      this.handleSetError({
        vinRz: {
          error_type: 'invalid_format',
        },
      })
    }
    this.setState({ pushIsPristine: false })
  }

  handleReset = () => {
    const { carInfoRequestSuccess, onErrorsReset } = this.props
    carInfoRequestSuccess(null)
    this.handleChange('vinRz', '')
    onErrorsReset()
    window.scrollTo(0, 0)
  }

  handleShowCalculator = () => {
    const { carInfoRequestSuccess, onErrorsReset } = this.props
    carInfoRequestSuccess(null)
    this.handleChange('vinRz', '')
    onErrorsReset()
    document.getElementById('calculator').scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
  }

  render() {
    const { carInfo, data, validationErrors, remoteValidationErrors, errors, activeSettings } = this.props
    const { openSuccessErrorModal } = this.state

    const combinedValidationErrors = Object.assign({ ...validationErrors }, { ...remoteValidationErrors })
    const rzOrVinRequired =
      (this.props.validationErrors.modalRZ && Object.keys(this.props.validationErrors.modalRZ).length > 0) ||
      (this.props.validationErrors.modalVIN && Object.keys(this.props.validationErrors.modalVIN).length > 0)

    if (!openSuccessErrorModal) {
      return null
    }

    const imagePath = (activeSettings.baseImagePath || '').replace('all', 'sro')

    return (
      <form className="position-relative" onSubmit={this.handleSubmit}>
        <FormClient
          activeSettings={activeSettings}
          combinedValidationErrors={combinedValidationErrors}
          data={data}
          fetching={this.props.fetching}
          handleChange={this.handleChange}
          handleChangeValidationError={this.handleChangeValidationError}
          handleOnBlur={this.handleOnBlur}
          handleReset={this.handleReset}
          handleShowCalculator={this.handleShowCalculator}
          handleSubmit={this.handleSubmit}
          imagePath={imagePath}
          modalRz={this.modalRz}
          modalVin={this.modalVin}
          pushIsPristine={this.pushIsPristine}
          rzOrVinRequired={rzOrVinRequired}
          view="vehicleInfo"
        />
        <div className="container timeline-container mb-4">
          <h2 className="text-center mb-5">{I18n.translate('vehicleInfoGeneral.clientContainer.title')}</h2>
          <div className="row">
            <div className="col-md-4 tl-line">
              <div className="media">
                <img
                  alt="stopwatch"
                  className="mr-4 mb-4"
                  height="66"
                  loading="lazy"
                  src={`${imagePath}/timeline-ico-stopwatch.png`}
                  width="66"
                />
                <div className="media-body pb-3 pb-md-5">
                  {I18n.translate('vehicleInfoGeneral.clientContainer.section.1')}
                </div>
              </div>
            </div>
            <div className="col-md-4 tl-line2 d-block d-md-none">
              <div className="media h-100">
                <img
                  alt="hand"
                  className="mr-4 mb-4 mb-md-0"
                  height="66"
                  loading="lazy"
                  src={`${imagePath}/timeline-ico-hand.png`}
                  width="66"
                />
                <div className="media-body pb-3 pt-md-5 align-text-bottom h-100">
                  {I18n.translate('vehicleInfoGeneral.clientContainer.section.2')}
                </div>
              </div>
            </div>
            <div className="col-md-4 tl-line">
              <div className="media">
                <img
                  alt="cards"
                  className="mr-4 mb-4"
                  height="66"
                  loading="lazy"
                  src={`${imagePath}/timeline-ico-cards.png`}
                  width="66"
                />
                <div className="media-body pb-3 pb-md-5">
                  {I18n.translate('vehicleInfoGeneral.clientContainer.section.3')}
                </div>
              </div>
            </div>
            <div className="col-md-4 tl-line2 d-block d-md-none">
              <div className="media">
                <img
                  alt="mouse"
                  className="mr-4 mb-4 mb-md-0"
                  height="66"
                  loading="lazy"
                  src={`${imagePath}/timeline-ico-free.png`}
                  width="66"
                />
                <div className="media-body pb-3 pb-md-5">
                  {I18n.translate('vehicleInfoGeneral.clientContainer.section.4')}
                </div>
              </div>
            </div>
            <div className="col-md-4 d-none d-md-block tl-line">
              <div className="media">
                <img
                  alt="mouse"
                  className="mr-4 mb-4"
                  height="66"
                  loading="lazy"
                  src={`${imagePath}/timeline-ico-mouse.png`}
                  width="66"
                />
                <div className="media-body pb-3 pb-md-5">
                  {I18n.translate('vehicleInfoGeneral.clientContainer.section.5')}
                </div>
              </div>
            </div>
            <div className="col-md-4 d-block d-md-none">
              <div className="media">
                <img
                  alt="mouse"
                  className="mr-4 mb-4"
                  height="66"
                  loading="lazy"
                  src={`${imagePath}/timeline-ico-mouse.png`}
                  width="66"
                />
                <div className="media-body pb-3 pb-md-5">
                  {I18n.translate('vehicleInfoGeneral.clientContainer.section.5')}
                </div>
              </div>
            </div>
          </div>
          <div className="row d-none d-md-block">
            <div className="col-md-12 mainline" />
          </div>
          <div className="row justify-content-md-center tl-2r">
            <div className="col-md-4 tl-line2 d-none d-md-block">
              <div className="media h-100">
                <img
                  alt="hand"
                  className="align-self-md-end mr-4 mb-4 mb-md-0"
                  height="66"
                  loading="lazy"
                  src={`${imagePath}/timeline-ico-hand.png`}
                  width="66"
                />
                <div className="media-body pt-md-5 mt-md-3 align-text-bottom h-100">
                  {I18n.translate('vehicleInfoGeneral.clientContainer.section.2')}
                </div>
              </div>
            </div>
            <div className="col-md-4 tl-line2 d-none d-md-block">
              <div className="media h-100">
                <img
                  alt="mouse"
                  className="align-self-md-end mr-4 mb-md-0"
                  height="66"
                  loading="lazy"
                  src={`${imagePath}/timeline-ico-free.png`}
                  width="66"
                />
                <div className="media-body pt-md-5 mt-md-3 align-text-bottom h-100">
                  {I18n.translate('vehicleInfoGeneral.clientContainer.section.4')}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div id="error-placeholder" />
        {carInfo || data.modalVIN || (errors && errors.length > 0) ? (
          <div className="client-container">
            <div className="fromClient__content pb-0">
              <div className="fromClient__close cursor-pointer pfp pfp-close" onClick={this.handleClose} />
              <div className="row mb-4">
                <div className="col-12 col-md-3">
                  <img
                    alt="robot"
                    className="fromClient__image"
                    src={`${imagePath}/${errors && errors.length > 0 ? 'robot-dont-know.png' : 'robot-magnifier.png'}`}
                  />
                </div>
                <div className="col-12 col-md-8">
                  {errors && errors.length > 0 ? (
                    errors.map((error) => (
                      <div key={error.error}>
                        <h3 className="fromClient__info-title failed">
                          {I18n.translate('vehicleInfoGeneral.fromClient.failed.title')}
                        </h3>
                        <p className="fromClient__info-text">
                          {I18n.translate(`vehicleInfoGeneral.validationErrors.${error.error}`)}
                        </p>
                        <a className="btn btn-link fromClient__link mb-4" onClick={this.handleReset}>
                          {I18n.translate('vehicleInfoGeneral.fromClient.failed.link')}
                        </a>
                      </div>
                    ))
                  ) : (
                    <div>
                      <h3 className="fromClient__info-title text-primary">
                        {I18n.translate('vehicleInfoGeneral.fromClient.success.title')}
                      </h3>
                      <p className="fromClient__info-text">
                        {I18n.translate('vehicleInfoGeneral.fromClient.success.description')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div />
        )}
      </form>
    )
  }
}

const mapStateToProps = (state: GlobalState) => ({
  autoFinish: state.car.autoFinish,
  carInfo: state.car.carInfo,
  data: state.car.data,
  errors: state.general.errors,
  fetching: state.general.fetching,
  remoteValidationErrors: state.validation.remoteValidationErrors,
  showPhoneValidation: state.car.showPhoneValidation,
  validationErrors: state.validation.validationErrors,
})

const mapDispatchToProps = {
  carInfoRequest: CarActions.carInfoRequest,
  carInfoRequestSuccess: CarActions.carInfoRequestSuccess,
  carValuationRequest: CarActions.carValuationRequest,
  onChangeRequestFailure: GeneralActions.onChangeRequestFailure,
  onChangeValidationError: ValidationActions.onChangeValidationError,
  onChangeValue: CarActions.onChangeValue,
  onErrorsReset: GeneralActions.onErrorsReset,
  onSetErrors: ValidationActions.onSetErrors,
  setAutoFinish: CarActions.setAutoFinish,
  setData: CarActions.setData,
  vinFromRzRequest: CarActions.vinFromRzRequest,
}

export default connect(mapStateToProps, mapDispatchToProps)(ClientContainer)
